/**
 * lib/audit.ts
 * ------------------------------------------------------------------
 * Append-only audit trail. Every financially or legally significant
 * action (issue, void, payment, settings change, …) writes one entry
 * here. BURS's model expects an auditable record of every document's
 * lifecycle, so this is wired from day one and called by each module.
 * ------------------------------------------------------------------
 */
import { collection } from 'firebase/firestore';
import { addDoc, db } from '@/lib/firebase';
import { COLLECTIONS, type AuditEntityType, type AuditEntry } from '@/types';

export interface LogAuditInput {
  actorUid: string;
  actorEmail: string;
  action: string;
  entityType: AuditEntityType;
  entityId: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

/**
 * Write an audit entry. Failures are swallowed (logged to console)
 * rather than thrown — an audit-write failure must never block the
 * user's actual operation, but we never silently skip the attempt.
 */
export async function logAudit(input: LogAuditInput): Promise<void> {
  try {
    const entry: Omit<AuditEntry, 'id'> = {
      timestamp: new Date().toISOString(),
      actorUid: input.actorUid,
      actorEmail: input.actorEmail,
      action: input.action,
      entityType: input.entityType,
      entityId: input.entityId,
      before: input.before,
      after: input.after,
      meta: input.meta,
    };
    await addDoc(collection(db, COLLECTIONS.audit), entry);
  } catch (err) {
    console.error('[audit] failed to write audit entry', input.action, err);
  }
}
